import { Component, ViewChild, inject, input, output } from '@angular/core';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { Paginator } from "src/app/shared/components/paginator/paginator";
import { PaginatorState } from 'primeng/paginator';
import { ButtonModule } from "primeng/button";
import { Popover, PopoverModule } from 'primeng/popover';
import { Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { BusinessListModel } from '@features/business/models/business.model';
import { BusinessLogo } from "@shared/components/business-logo/business-logo";
import { SortHeader } from "@shared/components/sort-header/sort-header";
import { SectorService } from '@shared/services/sector-service/sector-service';
import { MessageService } from 'primeng/api';
import { NotHasRolePipe } from '@shared/pipes/not-has-role.pipe';
import { UserRole } from '@core/auth/services/auth-service';

interface Action {
  key: string,
  label: string
}
@Component({
  selector: 'app-businesses-list',
  imports: [
    CommonModule,
    TableModule,
    Paginator,
    ButtonModule,
    PopoverModule,
    BusinessLogo,
    SortHeader,
    NotHasRolePipe
],
  templateUrl: './businesses-list.html',
  styleUrl: './businesses-list.scss',
  providers: [MessageService]
})
export class BusinessesList {
  @ViewChild('popover') popover!: Popover;

  router = inject(Router);
  private sectorService = inject(SectorService);
  private messageService = inject(MessageService);

  UserRole = UserRole;
  first = input<number>();
  rows = input<number>();
  data = input<BusinessListModel[]>([]);
  totalRecords = input<number>(0);
  selectedBusiness: BusinessListModel | null = null;

  pageChange = output<PaginatorState>();
  handleChangeBusinessStatus = output<BusinessListModel>();
  handleChangeBusinessGroupStatus = output<BusinessListModel>();

  sortBy = input<string | null>();
  sortOrder = input<string | null>();
  toggleSort = output<string>();

  // Mapa para almacenar los nombres de los sectores por key
  private sectorNamesMap: Map<string, string> = new Map();

  actionsOptions: Action[] = [
    {key: 'edit-business-group', label: 'Editar grupo empresarial'},
    {key: 'edit-business', label: 'Editar empresa'},
    {key: 'change-status-business-group', label: 'Cambiar estado grupo empresarial'},
    {key: 'change-status-business', label: 'Cambiar estado empresa'},
  ]

  get filteredActionsOptions(): Action[] {
    if (!this.selectedBusiness || this.selectedBusiness.GroupId === '') {
      // Si no hay grupo empresarial (ID = ""), excluir opciones relacionadas con el grupo
      return this.actionsOptions.filter(
        action => action.key !== 'edit-business-group' && action.key !== 'change-status-business-group'
      );
    }
    return this.actionsOptions;
  }

  constructor() {
    this.loadSectors();
  }

  private loadSectors() {
    this.sectorService.getSectors().subscribe({
      next: (sectors) => {
        // Crear un mapa de key -> nombre para acceso rápido
        sectors.forEach(sector => {
          this.sectorNamesMap.set(sector.value, sector.label);
        });
      }
    });
  }

  getSectorNames(sectorCodes: string[]): string {
    if (!sectorCodes || sectorCodes.length === 0) {
      return '';
    }

    // Convertir los códigos a nombres, usando el código si no se encuentra el nombre
    const names = sectorCodes.map(code =>
      this.sectorNamesMap.get(code) || code
    );

    return names.join(', ');
  }

  toggle(event: Event) {
    this.popover.toggle(event);
  }

  selectOption(action: Action) {
    if (this.selectedBusiness !== null) {
      switch (action.key) {
        case 'edit-business-group':
          this.handleEditBusinessGroup(this.selectedBusiness.GroupId);
          break;
        case 'edit-business':
          this.handleEditBusiness(this.selectedBusiness.Id);
          break;
        case 'change-status-business-group':
          this.handleChangeBusinessGroupStatus.emit(this.selectedBusiness);
          break;
        case 'change-status-business':
          this.handleChangeBusinessStatus.emit(this.selectedBusiness);
          break;
      }
    }
    this.popover.hide();
  }

  handleEditBusinessGroup(groupId: string) {
    if (groupId) {
      this.router.navigate([Routes.businessesEditGroupPage.replace(':id', groupId)]);
    }
    else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ha ocurrido un error consultando el grupo. Por favor, comuníquese con soporte.'
      });
    }
  }

  handleEditBusiness(businessId: string) {
    if (businessId) {
      this.router.navigate([Routes.businessesEditPage.replace(':id', businessId)]);
    }
    else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ha ocurrido un error consultando la empresa. Por favor, comuníquese con soporte.'
      });
    }
  }

  displayRowActions(event: MouseEvent, business: BusinessListModel) {
    if (this.selectedBusiness?.Id === business.Id) {
      this.popover.hide();
      this.selectedBusiness = null;
    } else {
      this.selectedBusiness = business;
      this.popover.show(event);

      if (this.popover.container) {
        this.popover.align();
      }
    }
  }

  hidePopover() {
    this.popover.hide();
  }

  handleToggleSort(columnName: string) {
    this.toggleSort.emit(columnName);
  }
}
